"use client";
import Link from "~/bridge/ui/Link";
import Logo from "~/assets/da-logo-text.png";
import { SearchOverlay } from "../search/search-overlay";
import { BasketButton } from "./basket-button";
import { TopicNavigation } from "./topic-navigation";
import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../../app-context/provider";
import { CrystallizeImage } from "../item/CrystallizeImage";
import { Price } from "../price";
import useLocation from "~/bridge/ui/useLocation";
import Image from "next/image";
import { RichText } from "~/use-cases/contracts/RichText";
import { Ticker } from "./ticker";
import { fetchNavigationByFolders } from "~/use-cases/crystallize/read/fetchNavigation";
import { FailureIcon } from "../aplia/Toast/Icons/Icons";
import type { Image as ImageType } from "~/use-cases/contracts/Image";
import { Promobanner } from "~/use-cases/contracts/Promobanner";
import PromoBanner from "../aplia/Promobanner/Promobanner";
import ToastProgressBar from "../aplia/Toast/ToastProgressbar";

export const Header: React.FC<{
  ticker: RichText;
  promobanner: Promobanner;
  navigation: Awaited<ReturnType<typeof fetchNavigationByFolders>>;
  logo: ImageType;
  popularSearchTerms?: string[];
}> = ({ ticker, navigation, logo, promobanner, popularSearchTerms }) => {
  const {
    state: appContextState,
    dispatch: appContextDispatch,
    path,
    _t,
  } = useAppContext();
  let [isOpen, setIsOpen] = useState(false);
  let [isSearchOpen, setIsSearchOpen] = useState(false);
  let [isToastOpen, setIsToastOpen] = useState(false);
  let location = useLocation();
  // useRef instead of useState for timeouts – avoids extra re-renders
  const resetLastAddedTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resetOtcRejectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resetOutOfStockRejectTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setIsOpen(false);
    setIsToastOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (appContextState.latestAddedCartItems.length === 0) return;
    setIsToastOpen(true);
    clearTimeout(resetLastAddedTimeout.current);
    resetLastAddedTimeout.current = setTimeout(() => {
      appContextDispatch.resetLastAddedItems();
    }, 10000);
    return () => clearTimeout(resetLastAddedTimeout.current);
  }, [appContextState.latestAddedCartItems]);

  useEffect(() => {
    if (appContextState.addOtcRejectItems.length === 0) return;
    setIsToastOpen(true);
    clearTimeout(resetOtcRejectTimeout.current);
    resetOtcRejectTimeout.current = setTimeout(() => {
      appContextDispatch.resetOtcRejectItems();
    }, 10000);
    return () => clearTimeout(resetOtcRejectTimeout.current);
  }, [appContextState.addOtcRejectItems]);

  useEffect(() => {
    if (appContextState.addOutOfStockRejectItems.length === 0) return;
    setIsToastOpen(true);
    clearTimeout(resetOutOfStockRejectTimeout.current);
    resetOutOfStockRejectTimeout.current = setTimeout(() => {
      appContextDispatch.resetOutOfStockRejectItems();
    }, 10000);
    return () => clearTimeout(resetOutOfStockRejectTimeout.current);
  }, [appContextState.addOutOfStockRejectItems]);

  useEffect(() => {
    try {
      const cacheBusted = localStorage.getItem("cache-busted-10");
      if (!cacheBusted) {
        // Only remove the cart key – not all of localStorage
        localStorage.removeItem("cart_items");
        localStorage.setItem("cache-busted-10", "true");
      }
    } catch {
      /* Private Mode / storage blocked */
    }
  }, []);

  return (
    <header className="w-full mx-auto sticky z-50 left-0 top-0">
      <div className="fixed sm:top-2 sm:right-2 z-60 pt-20 ">
        {appContextState.latestAddedCartItems.length > 0 && isToastOpen && (
          <div className="border-[#dfdfdf] border rounded-md shadow-sm relative max-w-full sm:top-2 space-y-4 my-4 sm:right-2 bg-white z-60 p-6">
            <p className="font-bold text-md mb-3 pb-2">
              {_t("cart.addedProductToCart")}
            </p>
            {appContextState.latestAddedCartItems.map((item, index) => {
              return (
                <div
                  className="flex p-3 mt-1 items-center bg-grey2 gap-3"
                  key={index}
                >
                  <div className="max-w-[35px] max-h-[50px] img-container img-contain">
                    <CrystallizeImage
                      {...item.images?.[0]}
                      sizes="100px"
                      fallbackAlt={item.name}
                      key={item.id}
                    />
                  </div>
                  <div>
                    <p className="text-sm">{item.name}</p>
                    <div className="text-sm font-bold">
                      <Price variant={item} size="small" />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex gap-3 mt-3 items-center border-t pt-2 border-t-[#dfdfdf]">
              <Link
                to={path("/checkout")}
                data-testid="go-to-cart-button"
                className="button text-sm font-bold py-2 px-4 rounded-md"
              >
                {_t("cart.goto")}
              </Link>
            </div>
            <ToastProgressBar
              autoCloseTimer={10}
              resetKey={
                Date.now() + appContextState.latestAddedCartItems.length
              }
            />
          </div>
        )}
        {appContextState.addOtcRejectItems.length > 0 && isToastOpen && (
          <div className="border-[#dfdfdf] border rounded-md shadow-sm relative max-w-full sm:top-2 space-y-4 my-4 sm:right-2 bg-white z-60 p-6">
            <p className="font-bold text-md mb-3 pb-2">
              {_t("cart.otc.bannerTitle")}
            </p>
            <div className="flex p-3 mt-1 items-center bg-grey2 gap-3" key={1}>
              <div className="max-w-[35px] max-h-[50px] img-container img-contain fill-red-600 w-[35px]">
                <FailureIcon />
              </div>
              <div>
                <p className="text-sm font-bold">
                  {_t("cart.otc.headerTitle")}
                </p>
                <div className="text-sm">
                  {_t("cart.otc.readMorePrompt")}
                  <br />
                  <Link to={path("/informasjon/reseptfrie-legemidler")}>
                    {_t("readMore")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-3 items-center border-t pt-2 border-t-[#dfdfdf]">
              <Link
                to={path("/checkout")}
                data-testid="go-to-cart-button"
                className="button text-sm font-bold py-2 px-4 rounded-md"
              >
                {_t("cart.goto")}
              </Link>
            </div>
            <ToastProgressBar
              autoCloseTimer={10}
              resetKey={Date.now() + appContextState.addOtcRejectItems.length}
            />
          </div>
        )}
        {appContextState.addOutOfStockRejectItems.length > 0 && isToastOpen && (
          <div className="border-[#dfdfdf] border rounded-md shadow-sm relative max-w-full sm:top-2 space-y-4 my-4 sm:right-2 bg-white z-60 p-6">
            <p className="font-bold text-md mb-3 pb-2">
              {_t("cart.outofstock.bannerTitle")}
            </p>
            <div className="flex p-3 mt-1 items-center bg-grey2 gap-3" key={1}>
              <div className="max-w-[35px] max-h-[50px] img-container img-contain fill-red-600 w-[35px]">
                <FailureIcon />
              </div>
              <div>
                <p className="text-sm font-bold">
                  {_t("cart.outofstock.headerTitle")}
                </p>
              </div>
            </div>
            <ToastProgressBar
              autoCloseTimer={10}
              resetKey={
                Date.now() + appContextState.addOutOfStockRejectItems.length
              }
            />
          </div>
        )}
      </div>
      <div>
        <div className="header__bg justify-between-full">
          <Ticker ticker={ticker} />
          <div className="flex flex-auto items-center mx-auto px-6 xl:container pt-3 pb-2">
            <div className="flex mx-auto flex-auto justify-between items-center relative">
              <div className="flex gap-4 md:gap-10 items-center">
                <div className="main-logo-wrapper img-container flex items-center">
                  <Link to={path("/")} className="flex items-center">
                    <CrystallizeImage className="logo" {...logo} />
                  </Link>
                </div>
                <div
                  className={`main-menu gap-3 xl:gap-6 2xl:gap-10 xl:flex xl:items-center xl:flex-row flex-col xl:w-auto xl:h-auto xl:relative xl:px-0 xl:py-0 xl:mt-0 xl:top-0 top-10 mt-[3.1rem] w-full right-0 left-0 z-50 h-[calc(100vh-3.1em)] fixed bottom-0 px-10 py-10 ${
                    isOpen ? "flex overflow-auto" : "hidden"
                  }`}
                >
                  <TopicNavigation
                    navigation={navigation.catalogue.children ?? []}
                  />
                </div>
              </div>
            </div>

            <div className="header__icons flex flex-auto items-center justify-end h-[40px] relative pl-4">
              {/* Search trigger – opens fullscreen overlay */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="bg-white h-10 w-10 min-w-10 rounded-full flex items-center justify-around align-middle cursor-pointer mr-1"
                aria-label="Åpne søk"
              >
                <svg
                  className="icon-search max-w-none inline"
                  aria-hidden="true"
                  width="20"
                  height="20"
                  viewBox="0 0 12 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.4033 11.2273L8.96475 8.78927C8.84486 8.66985 8.68253 8.6028 8.5133 8.6028C8.34407 8.6028 8.18173 8.66985 8.06184 8.78927C7.41927 9.41044 6.58876 9.80096 5.70041 9.89964C4.81207 9.99832 3.91607 9.7996 3.15279 9.33459C2.38952 8.86959 1.80208 8.16458 1.48252 7.33001C1.16296 6.49544 1.12932 5.57846 1.38688 4.72273C1.64443 3.867 2.17863 3.12086 2.90579 2.6012C3.63294 2.08154 4.51197 1.81773 5.40516 1.85108C6.29836 1.88444 7.15525 2.21308 7.8416 2.78553C8.52796 3.35797 9.005 4.14187 9.19798 5.01443C9.33627 5.64745 9.32203 6.30428 9.15642 6.93071C9.13686 7.00408 9.13195 7.08059 9.14196 7.15586C9.15197 7.23113 9.17671 7.30369 9.21478 7.3694C9.25284 7.43511 9.30347 7.49268 9.36379 7.53883C9.4241 7.58497 9.49292 7.61879 9.56631 7.63835C9.71452 7.67784 9.87235 7.65685 10.0051 7.58C10.1378 7.50315 10.2346 7.37672 10.2741 7.22854C10.5922 6.03301 10.474 4.76326 9.94055 3.64701C9.40711 2.53075 8.49336 1.64101 7.36318 1.13734C6.233 0.633672 4.96032 0.549027 3.77337 0.898584C2.58642 1.24814 1.56285 2.00903 0.886219 3.04481C0.209588 4.08058 -0.0758406 5.32348 0.0811204 6.55061C0.238081 7.77774 0.827164 8.90883 1.74273 9.74103C2.65829 10.5732 3.84044 11.0521 5.07717 11.0918C6.31391 11.1314 7.52433 10.7293 8.49136 9.9575L10.5858 12.0492C10.6395 12.103 10.7032 12.1456 10.7733 12.1747C10.8434 12.2038 10.9186 12.2188 10.9946 12.2188C11.0705 12.2188 11.1457 12.2038 11.2158 12.1747C11.286 12.1456 11.3497 12.103 11.4033 12.0492C11.4578 11.9955 11.501 11.9316 11.5305 11.861C11.56 11.7905 11.5752 11.7148 11.5752 11.6383C11.5752 11.5618 11.56 11.4861 11.5305 11.4156C11.501 11.345 11.4578 11.281 11.4033 11.2273Z"
                    fill="#000"
                  />
                </svg>
              </button>
              <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} popularSearchTerms={popularSearchTerms} />
              <Link
                to={path("/profile")}
                className="icon-link p-2 rounded-full"
              >
                <svg
                  className="icon-user w-[30px] h-[30px] max-w-none"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  aria-label="User icon"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.9168 18.4151L20.4552 16.2262C20.3354 15.65 20.0993 15.1044 19.7613 14.6226C19.4234 14.1408 18.9908 13.733 18.4899 13.4242L15.7167 11.7076C16.1474 11.1329 16.4152 10.4527 16.492 9.73863L16.712 7.66506C16.7717 7.10547 16.713 6.53959 16.5398 6.00415C16.3666 5.4687 16.0827 4.97567 15.7066 4.55705C15.3305 4.13843 14.8705 3.80359 14.3566 3.57425C13.8427 3.3449 13.2863 3.2262 12.7236 3.22583H12.28C11.7169 3.22569 11.1601 3.34403 10.6458 3.57314C10.1314 3.80226 9.67106 4.13704 9.29456 4.55571C8.91806 4.97439 8.63386 5.4676 8.46043 6.00329C8.28701 6.53898 8.22823 7.10517 8.28793 7.66506L8.51151 9.73863C8.5883 10.4527 8.85614 11.1329 9.28685 11.7076L6.51007 13.4242C6.00917 13.733 5.57653 14.1408 5.2386 14.6226C4.90067 15.1044 4.66456 15.65 4.54469 16.2262L4.0831 18.4151C4.01062 18.7582 4.01564 19.113 4.09781 19.4539C4.17997 19.7947 4.3372 20.1129 4.55802 20.3852C4.77885 20.6575 5.05768 20.8771 5.37419 21.0279C5.69069 21.1787 6.03687 21.2569 6.38746 21.2568H18.6125C18.9631 21.2569 19.3092 21.1787 19.6258 21.0279C19.9423 20.8771 20.2211 20.6575 20.4419 20.3852C20.6627 20.1129 20.82 19.7947 20.9021 19.4539C20.9843 19.113 20.9893 18.7582 20.9168 18.4151ZM10.6356 5.75017C10.8431 5.52052 11.097 5.3375 11.3804 5.21317C11.6639 5.08885 11.9705 5.02605 12.28 5.02893H12.7236C13.0336 5.02864 13.3403 5.0937 13.6235 5.21986C13.9068 5.34601 14.1603 5.53045 14.3675 5.76113C14.5747 5.99181 14.731 6.26357 14.8261 6.55869C14.9213 6.85381 14.9532 7.16566 14.9197 7.47393L14.6997 9.54389C14.6415 10.0862 14.3849 10.5879 13.9792 10.9524C13.5734 11.3169 13.0472 11.5186 12.5018 11.5186C11.9563 11.5186 11.4301 11.3169 11.0244 10.9524C10.6186 10.5879 10.362 10.0862 10.3038 9.54389L10.0838 7.47393C10.0459 7.16381 10.0755 6.84915 10.1708 6.55159C10.266 6.25403 10.4246 5.98062 10.6356 5.75017ZM19.0416 19.2374C18.9909 19.3025 18.9257 19.3549 18.8512 19.3906C18.7768 19.4263 18.695 19.4442 18.6125 19.4429H6.38746C6.30491 19.4442 6.22318 19.4263 6.14872 19.3906C6.07425 19.3549 6.00908 19.3025 5.95832 19.2374C5.90594 19.1759 5.86779 19.1036 5.84659 19.0257C5.8254 18.9478 5.82168 18.8661 5.83571 18.7866L6.29731 16.5976C6.37041 16.2605 6.51097 15.9417 6.71053 15.6603C6.91009 15.3789 7.16452 15.1409 7.4585 14.9604L10.751 12.9193C11.2957 13.1851 11.8938 13.3232 12.5 13.3232C13.1061 13.3232 13.7042 13.1851 14.249 12.9193L17.5414 14.9604C17.8334 15.1419 18.0858 15.3805 18.2834 15.6618C18.4811 15.9431 18.62 16.2614 18.6918 16.5976L19.1534 18.7866C19.1706 18.867 19.1694 18.9502 19.1501 19.0301C19.1307 19.11 19.0937 19.1845 19.0416 19.2482V19.2374Z"
                    fill="#FFFFFF"
                  />
                  <path
                    d="M15.9261 16.2083H14.123C13.6251 16.2083 13.2214 16.6119 13.2214 17.1098C13.2214 17.6077 13.6251 18.0114 14.123 18.0114H15.9261C16.424 18.0114 16.8276 17.6077 16.8276 17.1098C16.8276 16.6119 16.424 16.2083 15.9261 16.2083Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </Link>
              <BasketButton />
            </div>
            <div className="icon-link icon-link--burger z-50 p-[8px] h-[40px] w-[40px] ml-1 text-center rounded-full cursor-pointer xl:hidden block">
              <button
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                className="focus:outline-hidden"
                aria-controls="mobile-menu"
                aria-label="Mobile Menu"
                title="Mobile Menu"
                aria-expanded="false"
              >
                {!isOpen ? (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {promobanner && <PromoBanner promobanner={promobanner} />}
        </div>
      </div>
    </header>
  );
};
